import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import Chart from 'chart.js/auto';
import { BranchManagerDashboardService } from 'src/app/services/dashboard/branch-manager-dashboard.service';
import { ToastrService } from 'ngx-toastr';

export interface Transfer {
  makeModel: string;
  chassisNo: string;
  dispatchedOn: string;
  from?: string;
  to?: string;
  driver: string;
  status: 'pending' | 'completed' | 'overdue';
}

export interface IncomingOutgoingTransferApiItem {
  vehicle_id: string;
  make: string;
  model: string;
  chassis_no: string;
  location_name: string;
  dispatched_on: string;
  driver_name: string;
  status: string;
}

export interface CurrentWeekTransferApiItem {
  vehicle_id: string;
  make: string;
  model: string;
  chassis_no: string;
  from_location: string;
  to_location: string;
  dispatched_on: string;
  driver_name: string;
 received_flag: number;
}

type IncomingOutgoingFilter = 'all' | 'overdue';
type WeekFilter = 'all' | 'pending' | 'completed';
type SalesPeriod = 'thisWeek' | 'lastWeek';

@Component({
  selector: 'app-branch-manager-dashboard',
  templateUrl: './branch-manager-dashboard.component.html',
  styleUrls: ['./branch-manager-dashboard.component.css']
})
export class BranchManagerDashboardComponent implements OnInit{

  public isLoading: boolean = false;

  @ViewChild('WeeklySales') weeklySalesModalElement!: ElementRef;

  public weekly_sales_chart: any;
  public weekly_sales_data: any[] | null = null;

  // Replace these with data pulled from your service (e.g. in ngOnInit)
  incomingTransfers: Transfer[] = [];
  outgoingTransfers: Transfer[] = [];
  weekTransfers: Transfer[] = [];

  // Active filter per list
  incomingFilter: IncomingOutgoingFilter = 'all';
  outgoingFilter: IncomingOutgoingFilter = 'all';
  weekFilter: WeekFilter = 'all';

  salesPeriodFilter: SalesPeriod = 'thisWeek';

  incoming_page: number = 1;
  incoming_count: number = 0;
  incomingSearchText: string = '';

  outgoing_page: number = 1;
  outgoing_count: number = 0;
  outgoingSearchText: string = '';

  week_page: number = 1;
  week_count: number = 0;
  weekSearchText: string = '';

  public weekly_sales: any[] = [];

  weekly_sales_page: number = 1;
  weekly_sales_count: number = 0;
  weeklySalesSearchText: string = '';

  noOfVehiclesInSupplierStock:number=0;
  noOfVehiclesEnroute:number=0;
  noOfVehiclesAtCfs:number=0;
  noOfVehiclesSoldThisWeek:number=0;

  constructor(
    private branchManagerDashboardService: BranchManagerDashboardService,
    private toastr: ToastrService,
  ){}

  ngOnInit(): void {
    this.countUnitsInSupplierStock();
    this.countUnitsEnroute();
    this.countUnitsAtCFS();
    this.countUnitsSoldThisWeek();
    this.getIncomingTransfers();
    this.getOutgoingTransfers();
    this.getCurrentWeekTransfers();
    this.getWeeklySalesSummary();
  }

  /**
   * Count units in supplier's stock
   */
  countUnitsInSupplierStock(): void{
    this.isLoading = true;

    this.branchManagerDashboardService.countUnitsInSupplierStock().subscribe({
      next: (response) => {
        this.isLoading = false;

        if(response.status==='success'){
          this.noOfVehiclesInSupplierStock = Number(response.data);
        }else{
          this.isLoading = false;
          this.toastr.error('Unable to load units in supplier\'s stock!', 'Error!');
        }
      },
      error: (err) => {
        this.isLoading = false;
        this.toastr.error('Failed to load units in supplier\'s stock. '+err, 'Error!');
        console.error('Failed to load units in supplier\'s stock.', err);
      }
    });
  }

  /**
   * Count units en route
   */
  countUnitsEnroute(): void{
    this.isLoading = true;

    this.branchManagerDashboardService.countUnitsEnroute().subscribe({
      next: (response) => {
        this.isLoading = false;

        if(response.status==='success'){
          this.noOfVehiclesEnroute = Number(response.data);
        }else{
          this.isLoading = false;
          this.toastr.error('Unable to load units en route!', 'Error!');
        }
      },
      error: (err) => {
        this.isLoading = false;
        this.toastr.error('Failed to load units en route. '+err, 'Error!');
        console.error('Failed to load units en route.', err);
      }
    });
  }

  /**
   * Count units at CFS
   */
  countUnitsAtCFS(): void{
    this.isLoading = true;

    this.branchManagerDashboardService.countUnitsAtCFS().subscribe({
      next: (response) => {
        this.isLoading = false;

        if(response.status==='success'){
          this.noOfVehiclesAtCfs = Number(response.data);
        }else{
          this.isLoading = false;
          this.toastr.error('Unable to load units at CFS!', 'Error!');
        }
      },
      error: (err) => {
        this.isLoading = false;
        this.toastr.error('Failed to load units at CFS. '+err, 'Error!');
        console.error('Failed to load units at CFS.', err);
      }
    });
  }

  /**
   * Count units sold within the current week
   */
  countUnitsSoldThisWeek(): void{
    this.isLoading = true;

    this.branchManagerDashboardService.countUnitsSoldThisWeek().subscribe({
      next: (response) => {
        this.isLoading = false;

        if(response.status==='success'){
          this.noOfVehiclesSoldThisWeek = Number(response.data);
        }else{
          this.isLoading = false;
          this.toastr.error('Unable to load units sold within the current week. Please try gain!', 'Error!');
        }
      },
      error: (err) => {
        this.isLoading = false;
        this.toastr.error('Failed to load units sold within the current week. '+err, 'Error!');
        console.error('Failed to load units sold within the current week', err);
      }
    });
  }

  /**
   * Get incoming transfers
   */
  getIncomingTransfers(): void{
    this.isLoading = true;

    this.branchManagerDashboardService.getIncomingTransfers().subscribe({
      next: (response) => {
        this.isLoading = false;

        if(response.status==='success'){
          this.incomingTransfers = response.data.map(item => this.mapIncomingTransfer(item));

        }else{
          this.isLoading = false;
          this.toastr.error('Unable to load incoming transfers. Please try gain!', 'Error!');
        }
      },
      error: (err) => {
        this.isLoading = false;
        this.toastr.error('Failed to load incoming transfers. '+err, 'Error!');
        console.error('Failed to load incoming transfers', err);
      }
    });
  }

  mapIncomingTransfer(item: IncomingOutgoingTransferApiItem): Transfer {
    return {
      makeModel: `${item.make} ${item.model}`,
      chassisNo: item.chassis_no,
      dispatchedOn: item.dispatched_on,
      from: item.location_name,
      driver: item.driver_name,
      status: this.calculateStatus(item.dispatched_on),
    };
  }

  incomingPageChangeEvent(event: number){
    this.incoming_page = event;
  }

  incomingOnSearchTextChange(event: Event) {
    this.incomingSearchText = (event.target as HTMLInputElement).value;
    this.incoming_page = 1; // Reset to page 1 after applying filter
  }

  /**
   * Get outgoing transfers
   */
  getOutgoingTransfers(): void{
    this.isLoading = true;

    this.branchManagerDashboardService.getOutgoingTransfers().subscribe({
      next: (response) => {
        this.isLoading = false;

        if(response.status==='success'){
          this.outgoingTransfers = response.data.map(item => this.mapOutgoingTransfer(item));

        }else{
          this.isLoading = false;
          this.toastr.error('Unable to load outgoing transfers. Please try gain!', 'Error!');
        }
      },
      error: (err) => {
        this.isLoading = false;
        this.toastr.error('Failed to load outgoing transfers. '+err, 'Error!');
        console.error('Failed to load outgoing transfers', err);
      }
    });
  }

  mapOutgoingTransfer(item: IncomingOutgoingTransferApiItem): Transfer {
    return {
      makeModel: `${item.make} ${item.model}`,
      chassisNo: item.chassis_no,
      dispatchedOn: item.dispatched_on,
      to: item.location_name,
      driver: item.driver_name,
      status: this.calculateStatus(item.dispatched_on),
    };
  }

  calculateStatus(dispatchedOn: string): 'pending' | 'overdue' {
    const dispatchedDate = new Date(dispatchedOn);
    const now = new Date();

    const diffMs = now.getTime() - dispatchedDate.getTime();
    const diffDays = diffMs / (1000 * 60 * 60 * 24);

    return diffDays > 2 ? 'overdue' : 'pending';
  }

  outgoingPageChangeEvent(event: number){
    this.outgoing_page = event;
  }

  outgoingOnSearchTextChange(event: Event) {
    this.outgoingSearchText = (event.target as HTMLInputElement).value;
    this.outgoing_page = 1; // Reset to page 1 after applying filter
  }

  getCurrentWeekTransfers(): void{
    this.isLoading = true;

    this.branchManagerDashboardService.getThisWeekTransfers().subscribe({
      next: (response) => {
        this.isLoading = false;

        if(response.status==='success'){
          this.weekTransfers = response.data.map(item => this.mapCurrentWeekTransfer(item));

        }else{
          this.isLoading = false;
          this.toastr.error('Unable to load transfers within the current week. Please try gain!', 'Error!');
        }
      },
      error: (err) => {
        this.isLoading = false;
        this.toastr.error('Failed to load transfers within the current week. '+err, 'Error!');
        console.error('Failed to load transfers within the current week', err);
      }
    });
  }

  mapCurrentWeekTransfer(item: CurrentWeekTransferApiItem): Transfer {
    return {
      makeModel: `${item.make} ${item.model}`,
      chassisNo: item.chassis_no,
      dispatchedOn: item.dispatched_on,
      from: item.from_location,
      to: item.to_location,
      driver: item.driver_name,
      status: this.calculateWeekTransferStatus(item.received_flag),
    };
  }

  calculateWeekTransferStatus(receivedFlag: number): 'pending' | 'completed' {
  return receivedFlag === 1 ? 'completed' : 'pending';
  }

   weekPageChangeEvent(event: number){
    this.week_page = event;
  }

  weekOnSearchTextChange(event: Event) {
    this.weekSearchText = (event.target as HTMLInputElement).value;
    this.week_page = 1; // Reset to page 1 after applying filter
  }

  setIncomingFilter(filter: IncomingOutgoingFilter): void {
    this.incomingFilter = filter;
  }

  setOutgoingFilter(filter: IncomingOutgoingFilter): void {
    this.outgoingFilter = filter;
  }

  setWeekFilter(filter: WeekFilter): void {
    this.weekFilter = filter;
  }

  // ----- Filtered lists -----
  get filteredIncomingTransfers(): Transfer[] {
    if (this.incomingFilter === 'overdue') {
      return this.incomingTransfers.filter(t => t.status === 'overdue');
    }
    return this.incomingTransfers;
  }

  get filteredOutgoingTransfers(): Transfer[] {
    if (this.outgoingFilter === 'overdue') {
      return this.outgoingTransfers.filter(t => t.status === 'overdue');
    }
    return this.outgoingTransfers;
  }

  get filteredWeekTransfers(): Transfer[] {
    if (this.weekFilter === 'pending') {
      return this.weekTransfers.filter(t => t.status === 'pending');
    }
    if (this.weekFilter === 'completed') {
      return this.weekTransfers.filter(t => t.status === 'completed');
    }
    return this.weekTransfers;
  }

  // ----- Counters (for the filter button badges) -----
  get incomingAllCount(): number {
    return this.incomingTransfers.length;
  }

  get incomingOverdueCount(): number {
    return this.incomingTransfers.filter(t => t.status === 'overdue').length;
  }

  get outgoingAllCount(): number {
    return this.outgoingTransfers.length;
  }

  get outgoingOverdueCount(): number {
    return this.outgoingTransfers.filter(t => t.status === 'overdue').length;
  }

  get weekAllCount(): number {
    return this.weekTransfers.length;
  }

  get weekPendingCount(): number {
    return this.weekTransfers.filter(t => t.status === 'pending').length;
  }

  get weekCompletedCount(): number {
    return this.weekTransfers.filter(t => t.status === 'completed').length;
  }

  // ------For sales performance chart--------------
  setSalesPeriod(period: SalesPeriod): void {
    if (this.salesPeriodFilter === period) {
      return;
    }

    this.salesPeriodFilter = period;
    this.getWeeklySalesSummary();
  }

  getWeeklySalesSummary(): void {
    this.isLoading = true;

    this.branchManagerDashboardService.getWeeklySalesSummary(this.salesPeriodFilter).subscribe({
      next: (response) => {
        this.isLoading = false;

        if(response.status==='success'){
          this.weekly_sales_data = response.data;

          if (this.weekly_sales_data.length > 0) {
            const chartLabels = this.weekly_sales_data.map((item) => item.day_of_week);
            const chartData = this.weekly_sales_data.map((item) => item.units_sold);
            // Call the method to create the chart with dynamic data
            this.weeklySalesChart(chartLabels, chartData);
          }
        }else{
          this.isLoading = false;
          this.toastr.error('Unable to load weekly sales data. Please try gain!', 'Error!');
        }
      },
      error: (err) => {
        this.isLoading = false;
        this.toastr.error('Failed to load weekly sales data. '+err, 'Error!');
        console.error('Failed to load weekly sales data', err);
      }
    });
  }

  weeklySalesChart(labels: string[], data: number[]): void {
    // Destroy any existing chart instance before redrawing on the same canvas,
    // otherwise Chart.js throws "Canvas is already in use"
    if (this.weekly_sales_chart) {
      this.weekly_sales_chart.destroy();
    }

    this.weekly_sales_chart = new Chart('WeeklySalesChart', {
      type: 'bar',
      data: {
        labels: labels,
        datasets: [
          {
            label: this.salesPeriodFilter === 'thisWeek' ? 'Units Sold This Week' : 'Units Sold Last Week',
            data: data,
            borderWidth: 1,
            backgroundColor: 'rgba(34, 139, 34)',
          },
        ],
      },
      options: {
        onClick: (event, elements) => {
          if (elements.length > 0) {
            this. getWeeklySales();
          }
        },
      },
    });
  }

   getWeeklySales():void{
    this.isLoading=true;

    this.branchManagerDashboardService.getWeeklySales(this.salesPeriodFilter)
    .subscribe({
      next: (response) => {
        if(response.status==='success'){
          this.weekly_sales = response.data;
          this.isLoading=false;

          //Show modal
          if (this.weeklySalesModalElement) {
            (<any>$(this.weeklySalesModalElement.nativeElement)).modal('show');
          }
        }else if(response.status==='no_data'){
          this.isLoading=false;
         this.toastr.info('No units sold within the current week!', 'Info!');
        }else{
          this.toastr.error('Unable to fetch units sold within the current week. Please try gain!', 'Error!');
          this.isLoading=false;
        }
      },
      error: (error) => {
        this.isLoading=false;
        this.toastr.error('Unable to fetch units sold within the current week. '+error, 'Error!');
      }
    });
  }

   weeklySalesPageChangeEvent(event: number){
    this.weekly_sales_page = event;
  }

  weeklySalesOnSearchTextChange(event: Event) {
    this.weeklySalesSearchText = (event.target as HTMLInputElement).value;
    this.weekly_sales_page = 1; // Reset to page 1 after applying filter
  }

}
